import { createWorker } from "tesseract.js";
import { toast } from "sonner";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { DATE_RE, REF_RE } from "./summarize";
import { EMPTY_RECEIPT } from "./store";
import { decodeImage } from "./watermark";
import { kindOf, stamp } from "./util";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

let progressCb = null;
let workerP = null;
const getWorker = () => {
  if (!workerP) workerP = createWorker("eng", 1, { logger: (m) => progressCb?.(m) }).catch((e) => { workerP = null; throw e; });
  return workerP;
};

const ocr = async (src) => (await (await getWorker()).recognize(src)).data.text;

function toCanvas(img, max = 2600) {
  const r = Math.min(1, max / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * r); c.height = Math.round(img.height * r);
  const g = c.getContext("2d");
  g.fillStyle = "#fff"; g.fillRect(0, 0, c.width, c.height);
  g.drawImage(img, 0, 0, c.width, c.height);
  return c;
}

async function pdfText(blob) {
  const doc = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()), isEvalSupported: false, enableXfa: false }).promise;
  let text = "";
  for (let i = 1; i <= Math.min(doc.numPages, 15); i++) {
    const c = await (await doc.getPage(i)).getTextContent();
    text += c.items.map((it) => it.str + (it.hasEOL ? "\n" : " ")).join("") + "\n";
  }
  if (text.replace(/\s/g, "").length > 20) return text;
  let out = "";
  for (let i = 1; i <= Math.min(doc.numPages, 3); i++) {
    const page = await doc.getPage(i);
    const vp = page.getViewport({ scale: 2 });
    const c = document.createElement("canvas");
    c.width = vp.width; c.height = vp.height;
    await page.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
    out += (await ocr(c)) + "\n";
  }
  return out;
}

const MONEY = /\$?\s?((?:\d{1,3}(?:,\d{3})+|\d+)[.,]\d{2})\b/g;
const money = (s) => [...s.matchAll(MONEY)].map((m) => parseFloat(m[1].replace(/,(?=\d{3})/g, "").replace(",", ".")));
const CARRIERS = [["UPS", /\bups\b/i], ["USPS", /\busps|postal service\b/i], ["FedEx", /\bfed\s?ex\b/i], ["DHL", /\bdhl\b/i], ["Amazon Logistics", /\bamazon\b/i], ["Canada Post", /canada post/i]];

export function extractFields(text = "") {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const vendor = lines.slice(0, 6).find((l) => (l.match(/[a-z]/gi) || []).length >= 3 && !/receipt|invoice|welcome|tel|phone|www|http|date|order|thank|page|shipping|confirmation|packing|slip|tracking|delivery|notice|statement/i.test(l) && (l.match(/\d/g) || []).length < l.length / 3);
  const totals = lines.filter((l) => /(total|amount due|balance due|amount paid|grand total)/i.test(l) && !/sub\s?-?total/i.test(l)).flatMap(money);
  const all = money(text);
  const amount = totals.length ? totals[totals.length - 1] : all.length ? Math.max(...all) : null;
  const inv = text.match(/\b(?:invoice|inv|receipt|order|transaction|trans)\s*(?:no\.?|number|#|id)?\s*[:#]?\s*([A-Z0-9][A-Z0-9-]{3,})/i);
  let tracking = "", carrier = (CARRIERS.find(([, re]) => re.test(text)) || [""])[0];
  const ups = text.match(/\b1Z[0-9A-Z]{16}\b/), usps = text.match(/\b9[2-5]\d{18,20}\b/), fedex = text.match(/\b\d{12}(?:\d{3})?\b/);
  if (ups) { tracking = ups[0]; carrier = "UPS"; } else if (usps) { tracking = usps[0]; carrier = carrier || "USPS"; } else if (fedex && carrier === "FedEx") tracking = fedex[0];
  const ref = text.match(REF_RE);
  return {
    vendor: vendor ? vendor.replace(/\s+/g, " ").slice(0, 48) : "",
    amount: amount != null ? amount.toFixed(2) : "",
    date: (text.match(DATE_RE) || [""])[0],
    invoice: inv ? inv[1] : "",
    carrier, tracking,
    ref: ref ? ref[1] : "",
  };
}

export async function scanFile(blob, { name = "", mime = "" } = {}, onProgress) {
  progressCb = onProgress;
  const kind = kindOf(mime || blob.type, name);
  let text = "";
  if (kind === "pdf") text = await pdfText(blob);
  else if (kind === "photo") text = await ocr(toCanvas(await decodeImage(blob)));
  else if (/text\/plain/.test(mime || blob.type) || /\.txt$/i.test(name)) text = await blob.text();
  else throw new Error("Only photos, PDFs and text files can be scanned");
  text = text.replace(/[ \t]+/g, " ").replace(/\n{3,}/g, "\n\n").trim();
  return { text, fields: extractFields(text) };
}

export const scannable = (f) => f.kind === "photo" || f.kind === "pdf";

const LABELS = { vendor: "Vendor", amount: "Amount", date: "Date", invoice: "Invoice #", carrier: "Carrier", tracking: "Tracking #", ref: "Ref" };
export const describe = (fields) => Object.entries(fields).filter(([, v]) => v).map(([k, v]) => `${LABELS[k]}: ${k === "amount" ? "$" : ""}${v}`).join(" · ") || "Text added to note";

export function scanPatch(note, { text, fields }, fileName) {
  const receipt = { ...EMPTY_RECEIPT, ...note.receipt };
  ["vendor", "amount", "invoice", "carrier", "tracking"].concat(note.folderId === "receipts-tracking" && fields.date && /^\d{4}-\d{2}-\d{2}$/.test(fields.date) ? ["date"] : []).forEach((k) => { if (fields[k] && !receipt[k]) receipt[k] = fields[k]; });
  const patch = { receipt };
  if (fields.ref && !note.ref && note.folderId !== "daily-journal") patch.ref = fields.ref;
  if (!note.title) patch.title = fields.vendor ? `${fields.vendor}${fields.amount ? " Receipt" : ""}` : fileName.replace(/\.[^.]+$/, "");
  const header = `[Scanned from ${fileName} · ${stamp()}]`;
  const summary = describe(fields);
  patch.body = [note.body, `${header}\n${summary}\n\n${text}`].filter(Boolean).join("\n\n");
  return patch;
}

export async function scanWithToast(blob, meta) {
  const id = toast.loading(`Scanning ${meta.name || "file"}…`, { description: "Reading text on this device" });
  try {
    const res = await scanFile(blob, meta, (m) => {
      if (m.status === "recognizing text") toast.loading(`Reading text… ${Math.round(m.progress * 100)}%`, { id, description: meta.name });
      else if (/loading|initializ/.test(m.status)) toast.loading("Preparing scanner…", { id, description: "First scan downloads the reader once, then works offline" });
    });
    if (!res.text) { toast.error("No readable text found", { id, description: meta.name }); return null; }
    toast.success("Text extracted & note filled", { id, description: describe(res.fields) });
    return res;
  } catch (e) {
    toast.error("Scan failed", { id, description: e.message });
    return null;
  }
}
